import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export interface SelectOption<T = string> {
  value: T;
  label: string;
}

interface FilterSelectProps<T extends string = string> {
  label?: string;
  value: T | undefined;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  width?: 'sm' | 'md' | 'lg' | 'full';
}

/**
 * The field sizes itself to its content: never narrower than the configured
 * minimum, but growing to fit whichever is wider — its label or the selected
 * value. A fixed trigger width used to let long labels ("Columns broken down
 * by:") spill past the field they belong to.
 */
const widthClasses = {
  sm: 'w-fit min-w-24',
  md: 'w-fit min-w-32',
  lg: 'w-fit min-w-40',
  full: 'w-full'
} as const;

function FilterSelect<T extends string = string>({
  label,
  value,
  onChange,
  options,
  placeholder,
  className = '',
  width = 'md',
  disabled = false
}: FilterSelectProps<T>) {
  // Map empty string to a special value for Radix UI
  const EMPTY_VALUE = '__empty__';
  const mappedValue = value === '' ? EMPTY_VALUE : (value ?? '');

  const handleChange = (newValue: string) => {
    onChange((newValue === EMPTY_VALUE ? '' : newValue) as T);
  };

  return (
    <div className={`flex flex-col gap-2 ${widthClasses[width]} ${className}`}>
      {label && <Label className="whitespace-nowrap">{label}:</Label>}
      <Select value={mappedValue} onValueChange={handleChange} disabled={disabled}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => {
            const itemValue = option.value === '' ? EMPTY_VALUE : option.value;
            return (
              <SelectItem key={itemValue} value={itemValue}>
                {option.label}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
export default FilterSelect;
